import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Container,
  Box,
  Alert,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { GeneriqueData, Ligne_Document_Vente, SysTypeDocument, Tiers, WorkflowTransition } from '../models/models';
import { dispatchMultipleToNextDoc } from '../reduxStore/documentVenteSlice';
import { useSnackbar } from '../components/SnackbarProvider';
import { fetchLignesByDocumentId } from '../reduxStore/LigneDocumentVenteSlice';
import SelectDepot from '../components/Selects/SelectDepot';
import QuantiteInput from '../components/QuantiteInput';
import { fetchWorkflowTransitions } from '../reduxStore/WorkflowEtapeSlice';
import ClientAutoC from '../components/AutoComplete/ClientAutoC';
import FournisseurAutoC from '../components/AutoComplete/FournisseurAutoC';
import LockIcon from '@mui/icons-material/Lock';
import { TypeLigne, TypeTiers } from '../constants/enums';

interface LigneOF extends Ligne_Document_Vente {
  quantite_a_fabriquer: number;
  selected_depot: GeneriqueData | null;
}

// Référence stable : évite de recréer un tableau à chaque rendu (dépendance de useCallback/useEffect).
const NO_PERMISSIONS: string[] = [];

const NextDocDispatch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { ligneDOcData } = useSelector((state: any) => state.LigneDocumentVenteSlice);
  const { selectedDocument } = useSelector((state: any) => state.DocumentVenteSlice);
  const { selectedTypeDoc, sysTypeData } = useSelector((state: RootState) => state.SysTypeDocumentSlice);
  const { transitions } = useSelector((state: any) => state.WorkflowEtapeSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const permissions = useSelector((state: RootState) => state.AuthSlice.permissions) ?? NO_PERMISSIONS;

  // Le dispatch crée un document du type cible : il exige le droit de création de CE type,
  // pas seulement docVentes.create. Miroir de la garde backend (_checkDispatchTarget).
  const peutCreerType = useCallback(
    (code?: string | null) => !!code && permissions.includes(`documentVentes.${code}.create`),
    [permissions]
  );

  const currentTypeDoc = selectedDocument?.type_document ?? selectedTypeDoc;

  // Résolution des transitions depuis le workflow (priorité) ou fallback next_doc_code
  const [resolvedTransitions, setResolvedTransitions] = useState<WorkflowTransition[]>([]);
  const [selectedNextDocCode, setSelectedNextDocCode] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTypeDoc?.code) return;
    dispatch(fetchWorkflowTransitions({ source_code: currentTypeDoc.code, departement_id: selectedDepartement?.id ?? null }));
  }, [dispatch, currentTypeDoc?.code, selectedDepartement?.id]);

  useEffect(() => {
    if (transitions && transitions.length > 0) {
      setResolvedTransitions(transitions);
      // Un document peut avoir plusieurs cibles possibles : pré-sélectionner la première
      // AUTORISÉE, sinon l'utilisateur atterrit sur un type interdit avec un bouton
      // désactivé sans comprendre qu'une autre cible lui est ouverte.
      // Aucune autorisée → on garde la première pour que le motif du refus s'affiche.
      const premiereAutorisee = transitions.find((t: WorkflowTransition) => peutCreerType(t.next_doc_code));
      setSelectedNextDocCode((premiereAutorisee ?? transitions[0]).next_doc_code);
    } else {
      setResolvedTransitions([]);
      setSelectedNextDocCode(null);
    }
  }, [transitions, peutCreerType]);

  const nextDoc: SysTypeDocument | undefined = selectedNextDocCode
    ? sysTypeData.find((t: SysTypeDocument) => t.code === selectedNextDocCode)
    : undefined;

  const peutCreerNextDoc  = peutCreerType(nextDoc?.code);
  // Aucune des cibles proposées n'est ouverte à cet utilisateur → le select ne sert à rien.
  const aucuneCibleAutorisee = resolvedTransitions.length > 0
    && !resolvedTransitions.some((t) => peutCreerType(t.next_doc_code));

  const [lignesAvecQuantite, setLignesAvecQuantite] = useState<LigneOF[]>([]);
  const [globalDepot, setGlobalDepot] = useState<GeneriqueData | null>(null);
  const [selectedTiers, setSelectedTiers] = useState<Tiers | null>(null);

  // Le tiers est demandé si le doc cible l'exige ET que le doc source n'en a pas
  const tiersObligatoireCible = !!nextDoc?.tiers_obligatoire;
  const tiersSourceExiste     = !!selectedDocument?.tiers_id && selectedDocument.tiers_id > 0;
  const demanderTiers         = tiersObligatoireCible && !tiersSourceExiste;

  // Réinitialiser le tiers sélectionné quand on change de type de doc cible
  useEffect(() => {
    setSelectedTiers(null);
  }, [selectedNextDocCode]);

  useEffect(() => {
    if (ligneDOcData && ligneDOcData.length > 0) {
      // Seules les lignes ARTICLE sont dispatchables :
      //  - fn_quantite_restante / garde anti-dispatch-excessif ne s'applique qu'aux ARTICLE
      //  - REMISE / CHARGE n'ont pas de notion de "quantité restante" → risque de double facturation
      //  - COMMENTAIRE pas de quantité dispatchable
      //  - COMPOSANT régénéré automatiquement par la nomenclature dans le doc enfant si fabrication
      // L'opérateur ressaisit manuellement remises/charges/commentaires sur le doc enfant si besoin.
      const lignesArticle = ligneDOcData.filter(
        (l: Ligne_Document_Vente) => (l.type_ligne ?? TypeLigne.ARTICLE) === TypeLigne.ARTICLE
      );
      setLignesAvecQuantite(lignesArticle.map((ligne: Ligne_Document_Vente) => ({
        ...ligne,
        quantite_a_fabriquer: ligne.quantite_restante ?? 0,
        selected_depot: ligne.depot ?? null,
      })));
    }
  }, [ligneDOcData]);

  const handleQuantiteChange = (ligneId: number, value: string) => {
    const quantite = parseFloat(value) || 0;
    const ligne = lignesAvecQuantite.find((l) => l.id === ligneId);
    if (!ligne) return;
    const quantiteValide = Math.min(quantite, ligne.quantite_restante ?? 0);
    setLignesAvecQuantite((prev) =>
      prev.map((l) => l.id === ligneId ? { ...l, quantite_a_fabriquer: quantiteValide } : l)
    );
  };

  const handleGlobalDepotChange = (depot: GeneriqueData | null) => {
    setGlobalDepot(depot);
    if (depot) {
      setLignesAvecQuantite((prev) =>
        prev.map((l) => ({ ...l, depot_id: depot.id, selected_depot: depot }))
      );
    }
  };

  const handleLigneDepotChange = (ligneId: number, depot: GeneriqueData | null) => {
    setLignesAvecQuantite((prev) =>
      prev.map((l) =>
        l.id === ligneId ? { ...l, depot_id: depot?.id ?? l.depot_id, selected_depot: depot } : l
      )
    );
  };

  const handleCreer = async () => {
    const lignesSelectionnees = lignesAvecQuantite.filter((l) => l.quantite_a_fabriquer > 0);
    if (lignesSelectionnees.length === 0) {
      showSnackbar('Veuillez saisir au moins une quantité', 'warning');
      return;
    }
    if (!nextDoc) {
      showSnackbar('Aucun document suivant configuré pour ce type de document', 'error');
      return;
    }

    if (demanderTiers && !selectedTiers?.id) {
      showSnackbar(`Le ${nextDoc.type_tiers === TypeTiers.FOURNISSEUR ? 'fournisseur' : 'client'} est obligatoire pour ${nextDoc.libelle}.`, 'error');
      return;
    }

    const tiersIdFinal = demanderTiers
      ? selectedTiers!.id!
      : selectedDocument!.tiers_id;

    const groupesParDepot = lignesSelectionnees.reduce<Record<number, LigneOF[]>>((acc, ligne) => {
      const depotId = ligne.depot_id;
      if (!acc[depotId]) acc[depotId] = [];
      acc[depotId].push(ligne);
      return acc;
    }, {});

    const today = new Date().toISOString().split('T')[0];
    const groupes = Object.entries(groupesParDepot).map(([depotIdStr, lignesGroupe]) => {
      const depotId = Number(depotIdStr);
      return {
        document: {
          id: -1,
          contrat_id: selectedDocument!.contrat_id ?? null,
          numero_doc: '',
          date_doc: today,
          date_echeance: today,
          tiers_id: tiersIdFinal,
          reference: null,
          type_doc_id: nextDoc.id,
          doc_code: nextDoc.code,
          related_doc: selectedDocument!.id,
          depot_id: depotId,
          departement_id: selectedDocument!.departement_id,
          owner_id: selectedDocument!.owner_id,
          user_id: selectedDocument!.user_id,
          tiers: null,
          contrat: null,
          depot: null,
        },
        lignes: lignesGroupe.map((ligne) => ({
          id: -1,
          source_ligne_id: ligne.id!,
          document_vente_id: -1,
          article_id: ligne.article_id,
          extra_note: ligne.extra_note ?? '',
          quantite: ligne.quantite_a_fabriquer,
          prix_unitaire: ligne.prix_unitaire,
          remise: ligne.remise ?? 0,
          tva: ligne.tva ?? 0,
          depot_id: depotId,
          owner_id: selectedDocument!.owner_id,
          user_id: selectedDocument!.user_id,
        })),
      };
    });

    try {
      await dispatch(dispatchMultipleToNextDoc({ groupes })).unwrap();
      const nbDocs = groupes.length;
      showSnackbar(
        nbDocs > 1 ? `${nbDocs} documents créés (un par dépôt) !` : 'Document suivant créé avec succès !',
        'success'
      );
      dispatch(fetchLignesByDocumentId({
        limit: 100,
        offset: 0,
        document_vente_id: selectedDocument?.id ?? -1,
      }));
    } catch (error) {
      showSnackbar((error as string) || 'Erreur lors de la création', 'error');
    }
  };

  if (!selectedDocument) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info">Veuillez sélectionner un document de vente</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            Créer {nextDoc ? nextDoc.libelle : 'Document Suivant'} à partir de {currentTypeDoc?.libelle || 'Document Actuel'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Document : {selectedDocument.reference || 'N/A'}
          </Typography>
        </Box>

        {/* Sélection du type de document cible si plusieurs transitions */}
        {resolvedTransitions.length > 1 && (
          <Box mb={2}>
            <TextField
              select
              label="Document à créer"
              value={selectedNextDocCode ?? ''}
              onChange={(e) => setSelectedNextDocCode(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
            >
              {/* Les cibles interdites restent visibles mais grisées (même principe que
                  DepartementSelectionPage) : masquer laisserait croire à une transition
                  manquante dans le workflow. */}
              {resolvedTransitions.map((t) => {
                const autorise = peutCreerType(t.next_doc_code);
                return (
                  <MenuItem key={t.next_doc_code} value={t.next_doc_code} disabled={!autorise}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {!autorise && <LockIcon fontSize="small" color="error" />}
                      {t.next_doc_libelle ?? t.next_doc_code}
                      {!autorise && ' — non autorisé'}
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
        )}

        {aucuneCibleAutorisee && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Vous n'avez le droit de créer aucun des types de documents accessibles depuis
            {' '}{currentTypeDoc?.libelle || 'ce document'}.
          </Alert>
        )}

        {resolvedTransitions.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aucune transition configurée pour ce type de document.
            Configurez un workflow dans Paramètres → Workflows documents.
          </Alert>
        )}

        {/* Sélecteur de dépôt global */}
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
            Dépôt global :
          </Typography>
          <Box sx={{ width: 280 }}>
            <SelectDepot
              selectedDepot={globalDepot}
              onDepotChange={handleGlobalDepotChange}
              departement_id={selectedDocument.departement_id}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            (applique ce dépôt à toutes les lignes)
          </Typography>
        </Box>

        {/* Sélecteur de tiers — uniquement si le doc cible l'exige et que la source n'en a pas */}
        {demanderTiers && (
          <Box mb={3} display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
              {nextDoc?.type_tiers === TypeTiers.FOURNISSEUR ? 'Fournisseur' : 'Client'} :
            </Typography>
            <Box sx={{ width: 320 }}>
              {nextDoc?.type_tiers === TypeTiers.FOURNISSEUR ? (
                <FournisseurAutoC
                  selectedFournisseur={selectedTiers}
                  onFournisseurChange={setSelectedTiers}
                  required
                />
              ) : (
                <ClientAutoC
                  selectedClient={selectedTiers}
                  onClientChange={setSelectedTiers}
                  required
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              (obligatoire pour {nextDoc?.libelle})
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Table size="small" sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code article</TableCell>
              <TableCell>Désignation</TableCell>
              <TableCell align="right">Qté commandée</TableCell>
              <TableCell align="right">Qté restante</TableCell>
              <TableCell align="right">Qté à dispatcher</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Dépôt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lignesAvecQuantite.map((ligne) => {
              const restante = ligne.quantite_restante ?? 0;
              return (
                <TableRow key={ligne.id} hover>
                  <TableCell>{ligne.article?.code || 'N/A'}</TableCell>
                  <TableCell>{ligne.article?.designation || 'N/A'}</TableCell>
                  <TableCell align="right">{ligne.quantite || 0}</TableCell>
                  <TableCell align="right">{restante}</TableCell>
                  <TableCell>
                    <QuantiteInput
                      article={ligne.article}
                      value={ligne.quantite_a_fabriquer}
                      max={restante}
                      onChange={(v) => handleQuantiteChange(ligne.id!, String(v))}
                    />
                  </TableCell>
                  <TableCell>
                    <SelectDepot
                      selectedDepot={ligne.selected_depot}
                      onDepotChange={(depot) => handleLigneDepotChange(ligne.id!, depot)}
                      departement_id={selectedDocument.departement_id}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box mt={4} display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          {/* Cas résiduel : cible interdite alors que d'autres sont ouvertes (le select
              empêche de la choisir, mais l'état initial peut y mener si le workflow change). */}
          {nextDoc && !peutCreerNextDoc && !aucuneCibleAutorisee && (
            <Typography variant="caption" color="error">
              Vous n'avez pas le droit de créer un document de type {nextDoc.libelle}.
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreer}
            disabled={resolvedTransitions.length === 0 || !peutCreerNextDoc}
          >
            Créer {nextDoc ? nextDoc.libelle : 'Document Suivant'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default NextDocDispatch;
